/**
 * Cross-component trigger for the checkout flow.
 *
 * The checkout surface lives inside WorkshopTicketBlock (full-screen sheet
 * below `sm`, right-anchored drawer from `sm` up), but "reserve" CTAs are
 * scattered across the page (hero, persona cards, final CTA band, comparison
 * table). Rather than threading an opener through every layout, CTAs call
 * requestCheckout(), which fires a cancelable window event at EVERY
 * breakpoint:
 *
 *   - WorkshopTicketBlock listens while the event has bookable sessions and
 *     calls preventDefault() to mark the request handled — requestCheckout()
 *     then returns true and the caller should stop (preventDefault its own
 *     click, skip the anchor scroll).
 *   - With no listener (nothing bookable, or SSR) it returns false and the
 *     caller falls through to its legacy scroll-to-#tickets behavior.
 *
 * /membership uses the same contract for the AdVenture Plus checkout sheet,
 * with an optional `detail.plan` so a CTA can open it on a chosen plan.
 * Workshop listeners ignore the detail.
 */
import type { ProPlan } from "@shared/pro-pricing";

export const CHECKOUT_OPEN_EVENT = "adventure:open-checkout";

/** What a CTA may ask the checkout to preselect. */
export type CheckoutRequest = { plan?: ProPlan };

export function requestCheckout(detail?: CheckoutRequest): boolean {
  if (typeof window === "undefined") return false;
  const event = new CustomEvent<CheckoutRequest | undefined>(CHECKOUT_OPEN_EVENT, { cancelable: true, detail });
  window.dispatchEvent(event);
  return event.defaultPrevented;
}
